import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ListFilter, Plus } from 'lucide-react';
import type { Desk, DeskCreateBody } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Badge, Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { desksListOptions, errorMessage } from '@/features/desks/desks-queries';
import {
  matchesText,
  normalizeText,
  SettingsBreadcrumb,
  SettingsSearch,
} from '@/features/settings/settings-ui';

/** Chip Ativo/Inativo (ponto + rótulo, estilo TiFlux). */
export function DeskActiveDot({ active }: { active: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[13px]">
      <span
        className={cn('size-2 shrink-0 rounded-full', active ? 'bg-success' : 'bg-border')}
        aria-hidden
      />
      <span className={active ? undefined : 'text-muted-foreground'}>
        {active ? 'Ativo' : 'Inativo'}
      </span>
    </span>
  );
}

type StatusFilter = 'all' | 'active' | 'inactive';

const STATUS_FILTER_LABELS: Record<StatusFilter, string> = {
  all: 'Todos',
  active: 'Ativo',
  inactive: 'Inativo',
};

/**
 * /settings/desks — lista de Mesas de serviço fiel ao TiFlux: breadcrumb,
 * busca à direita + "+ Mesa", tabela Nome (link) | Nome de exibição | Status
 * (filtrável). Sem lixeira: a desativação fica no editar da mesa.
 */
export function DesksPage() {
  const list = useQuery(desksListOptions());
  const [createOpen, setCreateOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const q = normalizeText(query.trim());
  const desks = [...(list.data?.items ?? [])]
    .sort((a, b) => a.position - b.position)
    .filter((d) => (q === '' ? true : matchesText(d.name, q) || matchesText(d.displayName, q)))
    .filter((d) =>
      statusFilter === 'all' ? true : statusFilter === 'active' ? d.active : !d.active
    );

  return (
    <div className="rounded-lg border bg-card p-4 shadow-xs sm:p-5">
      <SettingsBreadcrumb page="Mesas de serviço" />
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <h1 className="text-xl leading-7 font-semibold">Mesas de serviço</h1>
        <div className="ml-auto flex items-center gap-2">
          <SettingsSearch value={query} onChange={setQuery} label="Buscar mesa" />
          <Button size="sm" className="h-9" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" strokeWidth={2.5} />
            Mesa
          </Button>
        </div>
      </div>

      <div className="mt-4">
        {list.isPending ? (
          <div aria-busy className="border-t">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="flex items-center gap-6 border-b border-divider px-4 py-3 last:border-0"
              >
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        ) : list.isError ? (
          <EmptyState
            className="border-t"
            message={`Erro ao carregar mesas: ${errorMessage(list.error)}`}
          />
        ) : desks.length === 0 ? (
          <EmptyState
            className="h-52 border-t"
            message={
              q !== '' || statusFilter !== 'all'
                ? 'Nenhuma mesa encontrada'
                : 'Nenhuma mesa cadastrada'
            }
          />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-10 px-4 font-semibold">Nome</th>
                  <th className="h-10 px-4 font-semibold">Nome de exibição</th>
                  <th className="h-10 px-4 font-semibold">
                    <span className="inline-flex items-center gap-1">
                      Status
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            title="Filtrar por status"
                            aria-label="Filtrar por status"
                            className={cn(
                              'flex size-6 items-center justify-center rounded-md transition-colors hover:bg-accent',
                              statusFilter !== 'all' ? 'text-primary' : 'text-muted-foreground'
                            )}
                          >
                            <ListFilter className="size-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          {(Object.keys(STATUS_FILTER_LABELS) as StatusFilter[]).map((f) => (
                            <DropdownMenuItem
                              key={f}
                              onSelect={() => setStatusFilter(f)}
                              className={f === statusFilter ? 'font-semibold' : undefined}
                            >
                              {STATUS_FILTER_LABELS[f]}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {desks.map((d) => (
                  <tr
                    key={d.id}
                    className="h-11 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                  >
                    <td className="max-w-72 truncate px-4">
                      <Link
                        to="/settings/desks/$deskId"
                        params={{ deskId: String(d.id) }}
                        className="font-medium text-primary-pressed hover:underline"
                      >
                        {d.name}
                      </Link>
                    </td>
                    <td className="max-w-72 truncate px-4">{d.displayName}</td>
                    <td className="px-4 whitespace-nowrap">
                      <span className="inline-flex items-center gap-2">
                        <DeskActiveDot active={d.active} />
                        {d.origin === 'tiflux' ? <Badge variant="info">Espelhada</Badge> : null}
                        {/* spec 017: etapas com situação no padrão, esperando a operação validar */}
                        {d.stagesToReview > 0 ? (
                          <Badge variant="warning" title="Etapas com situação a revisar">
                            {d.stagesToReview} a revisar
                          </Badge>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {createOpen ? <NewDeskDialog onClose={() => setCreateOpen(false)} /> : null}
    </div>
  );
}

function NewDeskDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');

  const create = useMutation({
    mutationFn: async (body: DeskCreateBody) => {
      const res = await api<Desk>('/desks', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (desk) => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'desks'] });
      onClose();
      void navigate({ to: '/settings/desks/$deskId', params: { deskId: String(desk.id) } });
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const body: DeskCreateBody = { name: name.trim() };
    if (displayName.trim()) body.displayName = displayName.trim();
    create.mutate(body);
  };

  return (
    <Dialog open onClose={onClose} title="Nova mesa">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="desk-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="desk-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="desk-display-name">Nome de exibição</Label>
          <Input
            id="desk-display-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Se vazio, usa o nome"
          />
        </div>
        {create.error ? (
          <p className="text-sm text-destructive">{errorMessage(create.error)}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={create.isPending || !name.trim()}>
            {create.isPending ? 'Criando…' : 'Criar mesa'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
